/*
 * Copyright (c) 2020 Well Solutions SRL. All Rights Reserved.
 * This software is the proprietary information of Well Solutions SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Injectable, Optional } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { Observable } from 'rxjs';
import { LoginService } from '../login/login.service';
import { UserContext } from '@irian-ro/util-user';

/**
 * Has to be added in app-routing.module, on root component, in canActivateChild tag
 * Works with routes inside lazy loaded modules, without having to be added separately in each module
 */

/**
 * @deprecated The guard should not be used
 */
@Injectable()
export class AuthGuardService {
  private static redirectUrlForErrors = '/auth/login';
  private static redirectUrlForMissingToken = "/auth/login";

  constructor(
    @Optional() private readonly router: Router,
    @Optional() private readonly loginService: LoginService,
    @Optional() private readonly userContext: UserContext
  ) {
    if (!userContext) {
      throw new Error('Provide UserContext in app.module');
    }
  }

  static updateConfig(config: { redirectUrlForErrors: string , redirectUrlForMissingToken: string}) {
    AuthGuardService.redirectUrlForErrors = config.redirectUrlForErrors;
    AuthGuardService.redirectUrlForMissingToken = config.redirectUrlForMissingToken;
  }

  /**
   * Method is called every time a route is activated
   * @param {ActivatedRouteSnapshot} route used to get the 'authorities' object from the routing.module
   * usage : in routing.module configure data object like
   *      path: 'authLogs',
   *      component: SharedAuthLogsComponent,
   *      data: {
   *          authorities: [['ROLE_UTILIZATORI', 'ROLE_PONTAJ'], ['ROLE_ADMIN']]
   *      }
   * User must have either ROLE_ADMIN OR both ROLE_UTILIZATORI AND ROLE_PONTAJ
   * HasAllRights is applied inside each array
   * HasAnyRightsGroup is applied on containing array
   * @param {RouterStateSnapshot} state used to navigate back to the accessed page after login
   * @returns {Observable<boolean> | Promise<boolean> | boolean} whether the user is authorized to access the route
   */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    const data = route.routeConfig['data'];
    let authorities: Array<string>;
    if (data) {
      authorities = data['authorities'];
    } else {
      authorities = route.routeConfig['authorities'];
    }
    if (state.url === '/auth/login') {
      this.loginService.logout().subscribe();
    }

    return this.checkLogin(authorities, state.url);
  }

  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.canActivate(route, state);
  }

  checkLogin(authorities: Array<string>, url: string): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      if (url == '/auth/login') {
        observer.next(true);
      } else {
        if (this.userContext.isLoggedIn()) {
          const userObservable = this.userContext.getLoggedInUserFromServer();

          userObservable.subscribe(
            (data: any) => {
              if (data) {
                let isAuthorized = true;

                if (!authorities || authorities.length === 0) {
                  isAuthorized = true;
                } else {
                  if (!this.userContext.hasAnyRightsGroup(authorities)) {
                    isAuthorized = false;
                  }
                }
                if (!isAuthorized) {
                  this.router.navigate([`/accessdenied`]);
                }

                observer.next(isAuthorized);
              }
            },
            () => {
              this.router.navigate([AuthGuardService.redirectUrlForErrors], {
                queryParams: { returnUrl: url },
              });
              observer.next(false);
            }
          );
        } else {
          this.router.navigate([AuthGuardService.redirectUrlForMissingToken], {
            queryParams: { returnUrl: url },
          });
          observer.next(false);
        }
      }
    });
  }
}
